"use client";

/**
 * Bottom control chrome for ClipVideoPlayer (play, skip, volume, speed,
 * quality, PiP, FS).
 */

import { cn } from "@v1/ui/utils";
import {
  Maximize,
  Minimize,
  Pause,
  PictureInPicture2,
  Play,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactElement, ReactNode } from "react";
import { useState } from "react";
import { formatClipDuration } from "../clip-format";
import type { ClipQualityState } from "./clip-quality-levels";
import { AUTO_QUALITY_INDEX } from "./clip-quality-levels";
import type { ClipMediaSnapshot } from "./clip-video-player-types";
import { PLAYBACK_RATES, SKIP_SECONDS } from "./clip-video-player-types";
import { ClipVideoScrubber } from "./clip-video-scrubber";

/** Only one popover at a time; both push their state up so chrome stays visible. */
type OpenMenu = "speed" | "quality" | null;

export function ClipVideoControls({
  snapshot,
  quality,
  displayTime,
  progressRatio,
  bufferedRatio,
  effectiveVolume,
  controlsVisible,
  fullscreen,
  pipSupported,
  showFullscreen,
  seekingDisabled,
  onTogglePlay,
  onSkip,
  onToggleMute,
  onChangeVolume,
  onSetPlaybackRate,
  onTogglePip,
  onToggleFullscreen,
  onScrubbingChange,
  onScrubTimeChange,
  onSeek,
  onSpeedMenuChange,
  onQualityMenuChange,
}: {
  readonly snapshot: ClipMediaSnapshot;
  readonly quality: ClipQualityState;
  readonly displayTime: number;
  readonly progressRatio: number;
  readonly bufferedRatio: number;
  readonly effectiveVolume: number;
  readonly controlsVisible: boolean;
  readonly fullscreen: boolean;
  readonly pipSupported: boolean;
  /** False on iPhone with the seeking lock — native fullscreen would seek. */
  readonly showFullscreen: boolean;
  /** Hides scrubber + skip buttons; the viewer can only watch linearly. */
  readonly seekingDisabled: boolean;
  readonly onTogglePlay: () => void;
  readonly onSkip: (delta: number) => void;
  readonly onToggleMute: () => void;
  readonly onChangeVolume: (next: number) => void;
  readonly onSetPlaybackRate: (next: number) => void;
  readonly onTogglePip: () => void;
  readonly onToggleFullscreen: () => void;
  readonly onScrubbingChange: (scrubbing: boolean) => void;
  readonly onScrubTimeChange: (time: number | null) => void;
  readonly onSeek: (time: number) => void;
  readonly onSpeedMenuChange: (open: boolean) => void;
  readonly onQualityMenuChange: (open: boolean) => void;
}): ReactElement {
  const t = useTranslations("clips.clipVideoControls");
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const playing = !snapshot.paused;

  function setMenu(next: OpenMenu): void {
    setOpenMenu(next);
    onSpeedMenuChange(next === "speed");
    onQualityMenuChange(next === "quality");
  }

  // One rung is no choice — MP4 clips and single-rendition manifests hide it.
  const showQuality = quality.levels.length > 1;
  const activeLevel = quality.levels.find(
    (l) => l.index === quality.activeIndex,
  );
  const selectedLevel = quality.levels.find(
    (l) => l.index === quality.selectedIndex,
  );
  const qualityButtonLabel =
    selectedLevel?.label ?? activeLevel?.label ?? "Auto";

  return (
    <div
      className={cn(
        "absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/70 via-black/25 to-transparent px-3 pb-2.5 pt-10 transition-opacity duration-200",
        controlsVisible || !snapshot.started ? "opacity-100" : "opacity-0",
      )}
    >
      {seekingDisabled ? null : (
        <ClipVideoScrubber
          duration={snapshot.duration}
          displayTime={displayTime}
          progressRatio={progressRatio}
          bufferedRatio={bufferedRatio}
          onScrubbingChange={onScrubbingChange}
          onScrubTimeChange={onScrubTimeChange}
          onSeek={onSeek}
        />
      )}

      <div className="mt-1 flex items-center gap-1 text-white">
        <ControlButton
          onClick={onTogglePlay}
          label={playing ? t("pause") : t("abspielen")}
        >
          {playing ? (
            <Pause className="h-5 w-5" fill="currentColor" strokeWidth={0} />
          ) : (
            <Play className="h-5 w-5" fill="currentColor" strokeWidth={0} />
          )}
        </ControlButton>

        {seekingDisabled ? null : (
          <>
            <ControlButton
              onClick={() => onSkip(-SKIP_SECONDS)}
              label={t("k5SekundenZurueck")}
              className="hidden sm:flex"
            >
              <SkipIcon direction="back" />
            </ControlButton>
            <ControlButton
              onClick={() => onSkip(SKIP_SECONDS)}
              label={t("k5Sekunden")}
              className="hidden sm:flex"
            >
              <SkipIcon direction="forward" />
            </ControlButton>
          </>
        )}

        <div className="group/vol flex items-center">
          <ControlButton
            onClick={onToggleMute}
            label={effectiveVolume === 0 ? t("ton") : t("stummschalten")}
          >
            {effectiveVolume === 0 ? (
              <VolumeX className="h-5 w-5" />
            ) : (
              <Volume2 className="h-5 w-5" />
            )}
          </ControlButton>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={effectiveVolume}
            onChange={(e) => onChangeVolume(Number(e.target.value))}
            aria-label={t("lautstaerke")}
            className="clip-volume-range h-1 w-0 cursor-pointer opacity-0 transition-all duration-200 group-hover/vol:ml-1 group-hover/vol:w-16 group-hover/vol:opacity-100"
          />
        </div>

        <span className="ml-1 select-none text-[13px] font-medium tabular-nums text-white/90">
          {formatClipDuration(displayTime * 1000)} /{" "}
          {formatClipDuration(snapshot.duration * 1000)}
        </span>

        <div className="ml-auto flex items-center gap-1">
          {showQuality ? (
            <ControlMenu
              open={openMenu === "quality"}
              onOpenChange={(open) => setMenu(open ? "quality" : null)}
              buttonText={qualityButtonLabel}
              ariaLabel={t("qualitaet")}
              title={t("qualitaet")}
            >
              <MenuRow
                label={t("auto")}
                hint={activeLevel?.label}
                active={quality.selectedIndex === AUTO_QUALITY_INDEX}
                onClick={() => {
                  quality.setSelectedIndex(AUTO_QUALITY_INDEX);
                  setMenu(null);
                }}
              />
              {quality.levels.map((level) => (
                <MenuRow
                  key={level.index}
                  label={level.label}
                  active={quality.selectedIndex === level.index}
                  onClick={() => {
                    quality.setSelectedIndex(level.index);
                    setMenu(null);
                  }}
                />
              ))}
            </ControlMenu>
          ) : null}

          <ControlMenu
            open={openMenu === "speed"}
            onOpenChange={(open) => setMenu(open ? "speed" : null)}
            buttonText={`${snapshot.playbackRate}×`}
            ariaLabel="Wiedergabegeschwindigkeit"
            title={t("geschwindigkeit")}
          >
            {PLAYBACK_RATES.map((r) => (
              <MenuRow
                key={r}
                label={`${r}×`}
                active={snapshot.playbackRate === r}
                onClick={() => {
                  onSetPlaybackRate(r);
                  setMenu(null);
                }}
              />
            ))}
          </ControlMenu>

          {pipSupported ? (
            <ControlButton
              onClick={onTogglePip}
              label={t("bildBild")}
              className="hidden sm:flex"
            >
              <PictureInPicture2 className="h-[18px] w-[18px]" />
            </ControlButton>
          ) : null}

          {showFullscreen ? (
            <ControlButton
              onClick={onToggleFullscreen}
              label={fullscreen ? t("vollbildVerlassen") : t("vollbild")}
            >
              {fullscreen ? (
                <Minimize className="h-[18px] w-[18px]" />
              ) : (
                <Maximize className="h-[18px] w-[18px]" />
              )}
            </ControlButton>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Text-trigger popover shared by the speed and quality menus. */
function ControlMenu({
  open,
  onOpenChange,
  buttonText,
  ariaLabel,
  title,
  children,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly buttonText: string;
  readonly ariaLabel: string;
  readonly title: string;
  readonly children: ReactNode;
}): ReactElement {
  const t = useTranslations("clips.clipVideoControls");
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        className="flex h-8 min-w-8 items-center justify-center rounded-md px-1.5 text-[13px] font-semibold tabular-nums text-white transition-colors hover:bg-white/15"
        aria-label={ariaLabel}
      >
        {buttonText}
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-label={t("menueSchliessen")}
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => onOpenChange(false)}
          />
          <div className="absolute bottom-full right-0 z-40 mb-2 w-36 overflow-hidden rounded-xl border border-white/10 bg-neutral-900/95 p-0 shadow-xl backdrop-blur">
            <p className="px-2 py-1 text-[11px] font-medium text-white/50">
              {title}
            </p>
            {children}
          </div>
        </>
      ) : null}
    </div>
  );
}

function MenuRow({
  label,
  hint,
  active,
  onClick,
}: {
  readonly label: string;
  /** Muted trailing text — used by "Auto" to show what is actually playing. */
  readonly hint?: string;
  readonly active: boolean;
  readonly onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center justify-between rounded-none px-2 py-1.5 text-left text-[13px] transition-colors hover:bg-white/10",
        active ? "text-white" : "text-white/70",
      )}
    >
      <span className="tabular-nums">{label}</span>
      <span className="flex items-center gap-1.5">
        {hint ? (
          <span className="text-[11px] tabular-nums text-white/40">{hint}</span>
        ) : null}
        {active ? (
          <span className="h-1.5 w-1.5 rounded-full bg-action-500" />
        ) : null}
      </span>
    </button>
  );
}

function ControlButton({
  onClick,
  label,
  children,
  className,
}: {
  readonly onClick: () => void;
  readonly label: string;
  readonly children: ReactElement;
  readonly className?: string;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-md text-white transition-colors hover:bg-white/15",
        className,
      )}
    >
      {children}
    </button>
  );
}

function SkipIcon({
  direction,
}: {
  readonly direction: "back" | "forward";
}): ReactElement {
  const Icon = direction === "back" ? RotateCcw : RotateCw;
  return (
    <span className="relative flex h-5 w-5 items-center justify-center">
      <Icon className="h-5 w-5" strokeWidth={2} />
      <span className="absolute text-[8px] font-bold leading-none">5</span>
    </span>
  );
}
